// WorkingAbroadPreparation.js
import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, Briefcase, Target, CheckCircle, AlertCircle, Globe, Users, FileText } from 'lucide-react';

const WorkingAbroadPreparation = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section with Image */}
      <section className="relative py-20 bg-gradient-to-br from-primary to-secondary">
        <div className="absolute inset-0">
          <div 
            className="absolute inset-0 bg-cover bg-center opacity-30"
            style={{ 
              backgroundImage: `url(${require('../../assets/blog-2.jpg')})`,
            }}
          />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-white"
          >
            <Link 
              to="/blog" 
              className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition"
            >
              <ArrowLeft size={20} />
              Back to Blog
            </Link>
            
            <div className="flex items-center gap-4 text-sm font-light mb-6">
              <div className="flex items-center gap-2">
                <Calendar size={18} />
                <span>July 18, 2024</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock size={18} />
                <span>7 min read</span>
              </div>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-semibold mb-6 leading-tight">
              Taking a Job Overseas: What to Sort Out, and in Which Order
            </h1>
            
            <p className="text-xl font-light leading-relaxed text-white/90">
              Most people start with the job adverts and hit the immigration rules later, which is the expensive way round. This piece runs the other way: visa route first, then your qualifications, then the market, then the interview.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="prose prose-lg max-w-none"
          >
            {/* Introduction */}
            <div className="mb-12">
              <h2 className="text-3xl font-semibold text-primary mb-4">Why This Is Not Just a Job Search</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                Looking for work in another country resembles looking for work at home for about the first week. After that the differences take over. A domestic search is a negotiation between you and an employer. An international one adds a third party who was never at the table and cannot be persuaded: the immigration system of the country you want to move to.
              </p>
              <p className="text-gray-600 leading-relaxed">
                That third party decides more than the employer does. It sets which occupations qualify, which employers are permitted to sponsor, what you must earn, and how long any of it takes. So it makes sense to start there, and to treat the search itself as the second question rather than the first.
              </p>
            </div>

            {/* Step One */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-gray-50 rounded-lg flex items-center justify-center">
                  <Globe className="text-primary" size={24} />
                </div>
                <h2 className="text-3xl font-semibold text-primary">Step One: Find Out If a Route Exists</h2>
              </div>
              
              <h3 className="text-xl font-semibold text-primary mb-4">Categories You Are Likely to Meet</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Every destination arranges this differently, but the permits tend to fall into two broad families, and which one applies changes everything downstream:
              </p>
              
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h4 className="font-semibold text-primary mb-3">Longer-term, employer-tied</h4>
                  <ul className="text-sm text-gray-600 space-y-2">
                    <li>• A named sponsor must offer the role first</li>
                    <li>• Salary and skill thresholds usually apply</li>
                    <li>• Some occupations sit on shortage lists</li>
                    <li>• Settlement may become possible in time</li>
                  </ul>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h4 className="font-semibold text-primary mb-3">Short-term or conditional</h4>
                  <ul className="text-sm text-gray-600 space-y-2">
                    <li>• Fixed projects, seasons or training periods</li>
                    <li>• Youth mobility and exchange arrangements</li>
                    <li>• Remote-work permits with income tests</li>
                    <li>• Rarely a path to staying permanently</li>
                  </ul>
                </div>
              </div>

              <h3 className="text-xl font-semibold text-primary mb-4">Questions to Answer Before Anything Else</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                If you cannot answer these for a given country, that country is not yet a real option:
              </p>
              
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Does my occupation appear</strong> on the list of roles that can be sponsored
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">What is the minimum salary</strong> attached to it, and is that realistic for my level
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Is my profession regulated there,</strong> requiring registration before I can practise
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">How long does the permit take,</strong> measured from a signed offer rather than from today
                  </div>
                </li>
              </ul>
            </div>

            {/* Step Two */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-gray-50 rounded-lg flex items-center justify-center">
                  <Users className="text-primary" size={24} />
                </div>
                <h2 className="text-3xl font-semibold text-primary">Step Two: Audit What You Are Offering</h2>
              </div>
              
              <h3 className="text-xl font-semibold text-primary mb-4">Your Profile in Someone Else's Terms</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                A qualification that means something at home may mean nothing, or something different, elsewhere. Work through each of these on its own:
              </p>
              
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Recognition:</strong> whether your degree or licence is accepted, and what the process is if it needs assessing
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Evidenced experience:</strong> years you can prove with letters and payslips, as opposed to years you can describe
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Language:</strong> the level the role needs day to day, and the level the permit requires on paper, which are often not the same
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Cash reserve:</strong> enough to cover the move, the deposit on somewhere to live, and the weeks before your first payslip clears
                  </div>
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-primary mb-4">Sorting Destinations Into Tiers</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Once you have both halves, the visa rules and your own profile, most people find their list sorts itself:
              </p>
              
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                  <h4 className="font-semibold text-primary mb-2">Act on now</h4>
                  <p className="text-sm text-gray-600">You already meet the permit criteria and employers are hiring at your level</p>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                  <h4 className="font-semibold text-primary mb-2">Close, with work</h4>
                  <p className="text-sm text-gray-600">One gap stands between you and eligibility, such as a language test or a registration</p>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                  <h4 className="font-semibold text-primary mb-2">Later, if at all</h4>
                  <p className="text-sm text-gray-600">Attractive places where the gap is measured in years rather than months</p>
                </div>
              </div>
            </div>

            {/* Step Three */}
            <div className="mb-12">
              <h2 className="text-3xl font-semibold text-primary mb-6">Step Three: Look at the Market, Narrowly</h2>
              
              <h3 className="text-xl font-semibold text-primary mb-4">Two or Three Countries, Properly</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Spreading a search across a dozen destinations produces a dozen shallow searches. Pick the ones from your top tier and go deep:
              </p>
              
              <div className="bg-gray-50 p-6 rounded-lg mb-8">
                <h4 className="font-semibold text-primary mb-4">What to Establish for Each:</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h5 className="font-medium text-gray-800 mb-2">Demand and pay</h5>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Who is actually hiring from overseas</li>
                      <li>• The pay band for your level, after tax</li>
                      <li>• Rent as a share of that figure</li>
                      <li>• Whether the sector is expanding or shedding</li>
                    </ul>
                  </div>
                  <div>
                    <h5 className="font-medium text-gray-800 mb-2">Conditions of work</h5>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Notice periods and probation terms</li>
                      <li>• Leave, sick pay and pension arrangements</li>
                      <li>• What happens to your status if you are let go</li>
                      <li>• How you would be taxed in both countries</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Step Four */}
            <div className="mb-12">
              <h2 className="text-3xl font-semibold text-primary mb-6">Step Four: Get the File Ready in Advance</h2>
              
              <h3 className="text-xl font-semibold text-primary mb-4">Documents That Take Longer Than You Think</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Once an employer is interested, the pace changes and you will be asked for paperwork within days. Have it waiting:
              </p>
              
              <div className="space-y-6 mb-8">
                <div className="border-l-4 border-primary pl-6">
                  <h4 className="font-semibold text-gray-800 mb-2">A CV in the local convention</h4>
                  <p className="text-gray-600">Length, whether a photograph is expected, and how much personal detail is normal all vary by country. Written results beat written responsibilities everywhere.</p>
                </div>
                <div className="border-l-4 border-primary pl-6">
                  <h4 className="font-semibold text-gray-800 mb-2">Certified qualifications</h4>
                  <p className="text-gray-600">Certificates and licences, translated and certified to the standard that destination accepts, not merely photocopied.</p>
                </div>
                <div className="border-l-4 border-primary pl-6">
                  <h4 className="font-semibold text-gray-800 mb-2">Proof of employment</h4>
                  <p className="text-gray-600">Letters on headed paper confirming dates, job titles and duties, plus contactable referees who know you are using their names.</p>
                </div>
                <div className="border-l-4 border-primary pl-6">
                  <h4 className="font-semibold text-gray-800 mb-2">Identity and status papers</h4>
                  <p className="text-gray-600">A passport with comfortable validity remaining, birth or marriage certificates where dependants are involved, and any police certificate the route requires.</p>
                </div>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg mb-8">
                <div className="flex items-start gap-3">
                  <AlertCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Worth knowing:</h4>
                    <p className="text-gray-600">Certification rules are unforgiving and vary widely. A translation accepted in one country may be rejected in the next for the wrong stamp. Confirm the exact requirement for your destination before you pay anyone to prepare documents.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Step Five */}
            <div className="mb-12">
              <h2 className="text-3xl font-semibold text-primary mb-6">Step Five: Interviews Across a Time Difference</h2>
              
              <h3 className="text-xl font-semibold text-primary mb-4">How These Usually Run</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Hiring from abroad tends to involve more stages than hiring locally, because the employer is carrying more risk:
              </p>
              
              <div className="bg-gray-50 p-6 rounded-lg mb-8">
                <div className="space-y-4">
                  <div className="pb-3 border-b border-gray-200">
                    <span className="font-medium text-gray-800">A short screening call</span>
                    <p className="text-sm text-gray-600 mt-1">Often with a recruiter rather than the hiring manager, checking availability, salary and whether you can be sponsored</p>
                  </div>
                  <div className="pb-3 border-b border-gray-200">
                    <span className="font-medium text-gray-800">A practical exercise</span>
                    <p className="text-sm text-gray-600 mt-1">Set remotely and often untimed, standing in for the trial day they cannot give you</p>
                  </div>
                  <div className="pb-3 border-b border-gray-200">
                    <span className="font-medium text-gray-800">A conversation about fit</span>
                    <p className="text-sm text-gray-600 mt-1">Less about culture in the abstract, more about how you handle disagreement, ambiguity and being new</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-800">A final panel</span>
                    <p className="text-sm text-gray-600 mt-1">Where relocation, start dates and the sponsorship itself are usually discussed openly</p>
                  </div>
                </div>
              </div>

              <h3 className="text-xl font-semibold text-primary mb-4">Preparing for the Format, Not Just the Questions</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                The content matters, but candidates lose ground on logistics more often than on answers:
              </p>
              
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Test the connection and the room</strong> at the hour the call is booked, not a different one
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Confirm the time zone in writing,</strong> and watch for clock changes falling between the booking and the call
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Have concrete examples ready,</strong> since remote interviews reward specifics and punish generalities
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Ask about the move itself,</strong> including who handles the permit and what support exists on arrival
                  </div>
                </li>
              </ul>
            </div>

            {/* After the offer */}
            <div className="mb-12">
              <h2 className="text-3xl font-semibold text-primary mb-6">When an Offer Comes</h2>
              
              <h3 className="text-xl font-semibold text-primary mb-4">Read It Twice, Then Read the Contract</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                The headline salary is the least informative part of an international offer. Look at the terms around it:
              </p>
              
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h4 className="font-semibold text-primary mb-3">The role and the risk</h4>
                  <ul className="text-sm text-gray-600 space-y-2">
                    <li>• Probation length and notice on either side</li>
                    <li>• Who pays the permit and any legal fees</li>
                    <li>• Whether costs are repayable if you leave early</li>
                    <li>• What happens to your status if the job ends</li>
                  </ul>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h4 className="font-semibold text-primary mb-3">Support for the move</h4>
                  <ul className="text-sm text-gray-600 space-y-2">
                    <li>• Flights, shipping and temporary housing</li>
                    <li>• Health cover from the first day or later</li>
                    <li>• Help with local registration and banking</li>
                    <li>• Provision for a partner or children</li>
                  </ul>
                </div>
              </div>

              <h3 className="text-xl font-semibold text-primary mb-4">The Weeks Before You Fly</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Accepting is not the end of the process. The remaining tasks are dull and time-sensitive:
              </p>
              
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Arrange somewhere to stay</strong> for the first month, before signing anything longer
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Sort out banking and tax residence</strong> in both countries, ideally before you leave
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Register for healthcare</strong> and carry proof of cover for the gap in between
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Keep digital copies of everything</strong> somewhere you can reach without your laptop
                  </div>
                </li>
              </ul>
            </div>

            {/* Conclusion */}
            <div className="mb-12">
              <h2 className="text-3xl font-semibold text-primary mb-6">In Short</h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                Taking a job abroad rewards preparation more than ambition. The people who find it straightforward are usually the ones who established, early and in writing, that a route existed for someone with their qualifications, and then spent their energy on the search rather than on discovering rules they should have read first.
              </p>
              <p className="text-gray-600 leading-relaxed mb-8">
                It is also worth saying that the answer is sometimes no, or not yet. Finding that out in week one costs you an afternoon. Finding it out after six months of applications costs considerably more.
              </p>
              
              <div className="bg-gradient-to-r from-primary to-secondary p-8 rounded-lg text-white">
                <h3 className="text-2xl font-semibold mb-4">Not Sure Which Routes Are Open to You?</h3>
                <p className="mb-6 text-white/90">
                  Ivipass will look at your qualifications and your target countries together, and tell you which combinations are worth pursuing and which are not.
                </p>
                <button className="bg-white text-primary px-6 py-3 rounded-lg font-medium hover:bg-gray-100 transition">
                  Check Your Eligibility
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default WorkingAbroadPreparation;
